import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, Copy } from 'lucide-react';
import { showUserError, showUserSuccess } from '@/utils/user-toast';
import { useMember } from '@/contexts/MemberContext';
import { extractFirstUrl } from '@/utils/url';


interface LocationState {
  topicName: string;
  memberType: string;
  downloadLink: string;
  remainingCycle: number;
  remainingToday: number;
}

const RedeemSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, updateQuota } = useMember();
  
  const state = location.state as LocationState | null;
  const isTotalMode = session?.member?.quota_mode === 'total';

  // 如果没有状态，返回首页
  useEffect(() => {
    if (!state) {
      navigate('/');
      return;
    }
    // 退出登录或会话失效后，浏览器后退不应重新展示成功页与资源内容
    if (!session) {
      navigate('/', { replace: true });
      return;
    }
    // 更新额度显示
    updateQuota(state.remainingCycle, state.remainingToday);
  }, [state, session, navigate, updateQuota]);

  // 复制内容
  const handleCopy = async () => {
    if (!state) return;
    try {
      await navigator.clipboard.writeText(state.downloadLink);
      showUserSuccess('已复制到剪贴板');
    } catch {
      showUserError('复制失败，请手动复制');
    }
  };

  // 打开链接（仅当内容中包含 HTTP/HTTPS 地址时使用）
  const handleOpen = () => {
    if (!state) return;
    const url = extractFirstUrl(state.downloadLink);
    if (!url) {
      showUserError('没有找到可直接打开的链接，请复制后到网盘 App 打开');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const hasOpenableUrl = !!state && !!extractFirstUrl(state.downloadLink);

  // 渲染期守卫：退出登录或会话失效后，即使浏览器后退恢复路由状态，
  // 成功页与资源内容也不会被渲染（effect 中的跳转仅作为兜底）。
  if (!state || !session) return null;

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center gap-6 px-5 pb-6 pt-12 sm:pt-16">
        {/* 成功标识 */}
        <div className="flex flex-col items-center gap-2.5 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-claimed-soft">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-claimed">
              <Check className="h-6 w-6 text-white" strokeWidth={3} />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-foreground">领取成功</h1>
          <p className="text-[15px] text-muted-foreground">{state.topicName}</p>
        </div>

        {/* 领取资源内容：微信里多数打不开网盘链接，所以“复制”是主按钮 */}
        <section className="w-full space-y-4 rounded-[20px] bg-white p-5 shadow-[0_10px_30px_rgba(80,50,20,0.07),0_0_0_1px_#F0E9DF]">
          <div className="max-h-36 overflow-y-auto whitespace-pre-wrap break-words text-[15px] leading-[1.8] text-foreground">
            {state.downloadLink}
          </div>
          <Button
            className="h-[52px] w-full rounded-[14px] bg-cta text-[17px] font-bold text-cta-foreground hover:bg-cta-hover"
            onClick={handleCopy}
          >
            <Copy className="h-[19px] w-[19px]" />
            {hasOpenableUrl ? '复制链接和提取码' : '复制全部内容'}
          </Button>
          {hasOpenableUrl && (
            <p className="text-center text-[13px] leading-relaxed text-muted-foreground">
              复制后打开网盘 App 即可领取
              <br />
              <button type="button" onClick={handleOpen} className="font-semibold text-orange-800">
                或尝试直接打开 ›
              </button>
            </p>
          )}
        </section>

        <p className="text-center text-xs text-muted-foreground/80">仅限本人学习使用 · 可在「领取记录」随时找回</p>
      </main>

      {/* 底部固定：不管内容多长，返回入口始终看得见 */}
      <footer className="sticky bottom-0 bg-background/95 px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur">
        <div className="mx-auto flex max-w-md flex-col items-center gap-1">
          <Button
            onClick={() => navigate('/', { replace: true })}
            variant="outline"
            className="h-[52px] w-full rounded-[14px] border-[1.5px] border-orange-200 bg-white text-base font-semibold text-foreground hover:bg-orange-50"
          >
            继续挑选主题
          </Button>
          <button
            type="button"
            onClick={() => navigate('/', { replace: true, state: { openHistory: true } })}
            className="h-10 px-3 text-sm text-muted-foreground"
          >
            查看领取记录
          </button>
        </div>
      </footer>
    </div>
  );
};

export default RedeemSuccessPage;
